import React from 'react';
import { Pressable } from 'react-native';
import Svg, { Path } from 'react-native-svg';

import { Box, Text, useTheme } from 'app/shopify/theme';

export type CheckboxSize = 'small' | 'large';
export type CheckboxState = 'enabled' | 'hover' | 'pressed' | 'focus' | 'disabled';

export interface CheckboxProps {
  label?: string;
  description?: string;
  showDescription?: boolean;
  showText?: boolean;
  selected?: boolean;
  size?: CheckboxSize;
  state?: CheckboxState;
  onPress?: () => void;
}

interface PressableInteractionState {
  pressed: boolean;
  hovered?: boolean;
}

function CheckIcon({ color }: { color: string }) {
  return (
    <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
      <Path d="M20 6L9 17L4 12" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function Checkbox({
  label = 'Checkbox label',
  description = 'Description',
  showDescription = false,
  showText = true,
  selected = false,
  size = 'small',
  state = 'enabled',
  onPress,
}: CheckboxProps) {
  const theme = useTheme();
  const isLarge = size === 'large';
  const isDisabled = state === 'disabled';
  const isInteractive = state === 'enabled' && onPress !== undefined;

  const labelVariant = isLarge ? 'bodyLarge' : 'bodyBase';
  const labelWidth = isLarge ? undefined : 113;
  const containerWidth = isLarge ? 168 : undefined;

  function getControlBorderColor(hovered: boolean, pressed: boolean) {
    if (isDisabled) return theme.colors['border.disabled'];
    if (!selected && (state === 'hover' || state === 'pressed' || hovered || pressed))
      return theme.colors['border.brand'];
    if (!selected) return theme.colors['border.primary'];
    return undefined;
  }

  function getControlBackground() {
    if (!selected) return theme.colors['surface.default'];
    if (isDisabled) return theme.colors['surface.disabled'];
    return theme.colors['surface.brand.default'];
  }

  function getLabelColor() {
    if (isDisabled) return theme.colors['text.disabled'];
    return theme.colors['text.primary'];
  }

  function getDescriptionColor() {
    if (isDisabled) return theme.colors['text.disabled'];
    return theme.colors['text.secondary'];
  }

  function getStateLayerColor(showHover: boolean, showPressed: boolean) {
    if (showPressed) return theme.colors['state-layer.press-brand'];
    if (showHover) return theme.colors['state-layer.hover-brand'];
    return undefined;
  }

  function renderInner(interaction?: PressableInteractionState) {
    const hovered = interaction?.hovered ?? false;
    const pressed = interaction?.pressed ?? false;
    const showHover = !isDisabled && (state === 'hover' || (isInteractive && hovered && !pressed));
    const showPressed = !isDisabled && (state === 'pressed' || (isInteractive && pressed));
    const layerColor = getStateLayerColor(showHover, showPressed);
    const showFocusRing = state === 'focus' && !isDisabled;
    const showOuterLayer = Boolean(layerColor) && showText;

    return (
      <Box
        position="relative"
        flexDirection="row"
        alignItems="flex-start"
        gap="spacing.general.x4"
        width={containerWidth}
      >
        {showOuterLayer ? (
          <Box
            position="absolute"
            top={-8}
            bottom={-8}
            left={isLarge ? -8 : -16}
            right={isLarge ? -9 : -16}
            style={{ backgroundColor: layerColor }}
          />
        ) : null}

        <Box
          width={24}
          height={24}
          borderRadius="radius.xs"
          borderWidth={selected ? 0 : 1}
          alignItems="center"
          justifyContent="center"
          position="relative"
          style={{
            backgroundColor: getControlBackground(),
            borderColor: getControlBorderColor(showHover, showPressed),
          }}
        >
          {selected ? (
            <CheckIcon color={isDisabled ? theme.colors['icon.disabled'] : theme.colors['icon.inverse.secondary']} />
          ) : null}

          {layerColor && !showText ? (
            <Box
              position="absolute"
              top={selected ? 0 : -1}
              right={selected ? 0 : -1}
              bottom={selected ? 0 : -1}
              left={selected ? 0 : -1}
              borderRadius="radius.xs"
              style={{ backgroundColor: layerColor }}
            />
          ) : null}

          {showFocusRing ? (
            <Box
              position="absolute"
              top={selected ? -4 : -5}
              left={selected ? -4 : -5}
              right={selected ? -4 : -5}
              bottom={selected ? -4 : -5}
              borderWidth={2}
              borderColor="border.focused"
              borderRadius="radius.sm"
            />
          ) : null}
        </Box>

        {showText ? (
          <Box
            minHeight={24}
            justifyContent="center"
            gap="spacing.general.x1"
            width={labelWidth}
            flex={isLarge ? 1 : undefined}
          >
            <Text
              variant={labelVariant}
              style={{
                color: getLabelColor(),
                fontFamily: isLarge ? theme.fonts.theFuture.medium : theme.textVariants[labelVariant].fontFamily,
              }}
            >
              {label}
            </Text>
            {showDescription ? (
              <Text variant="bodySmall" style={{ color: getDescriptionColor() }}>
                {description}
              </Text>
            ) : null}
          </Box>
        ) : null}
      </Box>
    );
  }

  if (isInteractive) {
    return (
      <Pressable onPress={onPress}>
        {(pressableState) =>
          renderInner({
            pressed: pressableState.pressed,
            hovered: 'hovered' in pressableState ? (pressableState as PressableInteractionState).hovered : false,
          })
        }
      </Pressable>
    );
  }

  return <Box>{renderInner()}</Box>;
}
